<template>
  <modal :show="show" class="modal-scroll-fix white-content" @update:show="$emit('update:show', $event)">
    <h4 class="card-title">{{ $t('tools.pickUser') }}</h4>
    <div>
      <div class="col-12 mb-3">
        <el-checkbox v-model="assignToYourself" @change="onAssignToYourselfChange">{{ $t('tools.assignToYourself') }}</el-checkbox>
      </div>
      <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
        <el-input
          v-model="localSearchQuery"
          clearable
          class="mb-3 search-input"
          :placeholder="$t('common.searchRecords')"
          @keyup.enter="$emit('search', $event)"
          @clear="$emit('clear-search')"
        >
          <template #prefix>
            <el-icon class="el-input__icon"><search /></el-icon>
          </template>
        </el-input>
      </div>
      <el-table :data="users" highlight-current-row @row-click="selectRow" class="clickable-rows">
        <el-table-column :label="$t('common.info')" min-width="150">
          <template #default="{ row }">
            <div>
              <div><strong>{{ $t('common.name') }}:</strong> {{ row.name }}</div>
              <div><strong>{{ $t('common.email') }}:</strong> {{ row.email }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column width="50">
          <template #default="{ row }">
            <el-radio v-model="localSelectedUser" :label="row.id"></el-radio>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap pagination">
      <div class="">
        <p class="card-category">
          {{ $t('pagination.showing') }} {{ usersFrom + 1 }} {{ $t('pagination.to') }} {{ usersTo }} {{ $t('pagination.of') }} {{ usersTotal }} {{ $t('pagination.entries') }}
        </p>
      </div>
      <material-pagination color="info" class="pagination-no-border">
        <material-pagination-item
          prev
          :disabled="localUsersPagination.currentPage === 1"
          @click="prevPage"
        />
        <material-pagination-item
          v-for="page in displayedPages"
          :key="page"
          :label="String(page)"
          :active="localUsersPagination.currentPage === page"
          @click="changePage(page)"
        />
        <material-pagination-item
          next
          :disabled="localUsersPagination.currentPage === totalPages"
          @click="nextPage"
        />
      </material-pagination>
    </div>
    <div class="row">
      <div class="col-sm d-flex justify-content-end">
        <material-button color="dark" fill @click="$emit('confirm')">{{ $t('common.confirm') }}</material-button>
      </div>
    </div>
  </modal>
</template>

<script>
import { ElTable, ElTableColumn, ElInput, ElRadio, ElCheckbox, ElIcon } from 'element-plus';
import { Search } from '@element-plus/icons-vue';
import Modal from './Modal.vue';
import MaterialPagination from '../MaterialPagination.vue';
import MaterialPaginationItem from '../MaterialPaginationItem.vue';
import MaterialButton from '../MaterialButton.vue';

export default {
  components: { Modal, MaterialPagination, MaterialPaginationItem, MaterialButton, ElTable, ElTableColumn, ElInput, ElRadio, ElCheckbox, ElIcon, Search },
  props: {
    show: { type: Boolean, required: true },
    users: { type: Array, required: true },
    selectedUser: { type: String, default: undefined },
    currentUserId: { type: String, default: undefined },
    searchQuery: { type: String, required: true },
    usersPagination: { type: Object, required: true },
    usersTotal: { type: Number, required: true }
  },
  emits: ['update:show', 'update:selectedUser', 'update:searchQuery', 'search', 'clear-search', 'update-page', 'confirm'],
  data() {
    return {
      localSelectedUser: this.selectedUser,
      localSearchQuery: this.searchQuery,
      localUsersPagination: { ...this.usersPagination },
      assignToYourself: this.selectedUser === this.currentUserId && !!this.currentUserId
    };
  },
  computed: {
    usersFrom() { return this.localUsersPagination.perPage * (this.localUsersPagination.currentPage - 1); },
    usersTo() {
      let highBound = this.usersFrom + this.localUsersPagination.perPage;
      return this.usersTotal < highBound ? this.usersTotal : highBound;
    },
    totalPages() {
      if (this.usersTotal > 0) {
        return Math.ceil(this.usersTotal / this.localUsersPagination.perPage);
      }
      return 1;
    },
    displayedPages() {
      const pages = [];
      const maxPages = Math.min(5, this.totalPages);

      let startPage = Math.max(1, this.localUsersPagination.currentPage - Math.floor(maxPages / 2));
      let endPage = startPage + maxPages - 1;

      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - maxPages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.localSearchQuery = this.searchQuery;
        this.localSelectedUser = this.selectedUser;
        this.localUsersPagination = { ...this.usersPagination };
        this.assignToYourself = this.selectedUser === this.currentUserId && !!this.currentUserId;
      }
    },
    localSelectedUser(newVal) {
      this.$emit('update:selectedUser', newVal);
      if (newVal !== this.currentUserId) {
        this.assignToYourself = false;
      }
    },
    localSearchQuery(newVal) {
      this.$emit('update:searchQuery', newVal);
      clearTimeout(this._searchDebounce);
      this._searchDebounce = setTimeout(() => {
        this.$emit('search');
      }, 400);
    },
    usersPagination: {
      handler(newVal) {
        this.localUsersPagination = { ...newVal };
      },
      deep: true
    }
  },
  methods: {
    onAssignToYourselfChange(checked) {
      if (checked) {
        this.localSelectedUser = this.currentUserId;
      }
    },
    selectRow(row) {
      this.localSelectedUser = row.id;
    },
    changePage(page) {
      if (page !== this.localUsersPagination.currentPage && page >= 1 && page <= this.totalPages) {
        this.localUsersPagination.currentPage = page;
        this.$emit('update-page', page);
      }
    },
    nextPage() {
      if (this.localUsersPagination.currentPage < this.totalPages) {
        this.changePage(this.localUsersPagination.currentPage + 1);
      }
    },
    prevPage() {
      if (this.localUsersPagination.currentPage > 1) {
        this.changePage(this.localUsersPagination.currentPage - 1);
      }
    }
  }
};
</script>

<style scoped>
.modal-scroll-fix {
  overflow-y: auto;
}
.clickable-rows :deep(.el-table__row) {
  cursor: pointer;
}
/* Hide el-radio's text label (a raw id) — previously hidden by a global style in RentLoanForm */
:deep(.el-radio__label) {
  display: none !important;
}
</style>
